import React, { useState, useMemo, useEffect, useRef } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import NumberInfo from 'views/Numbers/Details/common/NumberInfo';
import { BodyS, Caption } from 'components/Typography/Typography';
import CustomSearch from 'components/CustomSearch/CustomSearch';
import { SingleCorpUser } from 'utils/api/models/users/singleCorpUser';
import Badge from 'components/Badge/Badge';
import styles from 'assets/jss/material-dashboard-pro-react/views/numberDetails/detailsStyle';
import { makeStyles } from '@material-ui/core/styles';
import { CorpUserData } from 'utils/api/models/users/corpUserData';
import { ISearchElement } from 'components/CustomSearch/CustomSearch';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import ArrowForward from '@material-ui/icons/ArrowForward';
import Phone from '@material-ui/icons/PhoneOutlined';
import { finalize } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { useDispatch } from 'react-redux';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { setToast } from 'redux/modules/toast/actions';
import invalidateCache from 'utils/api/invalidateCache';
import { NumbersService } from 'services/numbers/numbers';
import { UsersService } from 'services/users/users';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IDepartmentData {
	name: string;
	color: string;
}

interface ISearchUserElement extends ISearchElement {
	departmentName: string;
	departmentUiColor: string;
	userId: string;
}

export interface IAssignUserDialogProps {
	isOpen: boolean;
	setIsOpen: (state: boolean) => void;
	phoneNumber: string;
	accountDetails: string;
	upAccountId: string;
	corpUserData: CorpUserData | null;
	fetchCorpUserData: () => void;
}

const AssignUserDialog = ({
	isOpen,
	setIsOpen,
	phoneNumber,
	accountDetails,
	upAccountId,
	corpUserData,
	fetchCorpUserData,
}: IAssignUserDialogProps) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const [selectedCorpUser, setSelectedCorpUser] = useState<ISearchUserElement | null>(null);
	const [changingDepartmentDialog, setChangingDepartmentDialog] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [corpUsers, setCorpUsers] = useState<SingleCorpUser[]>([]);
	const isMounted = useRef(true);

	const hideDialog = (): void => {
		setIsOpen(false);
	};

	const showDialog = (): void => {
		setIsOpen(true);
	};

	const changeIt = (): void => {
		setChangingDepartmentDialog(false);
		assignUserAction();
	};

	const cancel = (): void => {
		setChangingDepartmentDialog(false);
		showDialog();
	};

	const assign = (): void => {
		if (
			selectedCorpUser?.departmentName &&
			corpUserData?.departmentName !== selectedCorpUser?.departmentName &&
			!!selectedCorpUser
		) {
			hideDialog();
			setChangingDepartmentDialog(true);
		} else {
			assignUserAction();
		}
	};

	const assignUserAction = (): void => {
		if (!selectedCorpUser) {
			return;
		}
		dispatch(showLoadingIndicator());
		hideDialog();
		NumbersService.assignNumbersForCorpUserRestPost({
			userId: selectedCorpUser.userId,
			numbers: [phoneNumber],
		})
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(
				() => {
					dispatch(
						setToast({
							error: false,
							label: translate('numbers.setting.user.assigned'),
							actionType: translate('assigned'),
						})
					);
					invalidateCache(['getCorpUserDepartmentDataForNumber'], true);
					fetchCorpUserData();
				},
				(error) => throwError(error)
			);
	};

	useEffect(() => {
		setLoading(true);
		if (isOpen && upAccountId) {
			UsersService.getCorpUsersForNumber(upAccountId)
				.pipe(
					finalize(() => {
						if (isMounted.current) {
							setLoading(false);
						}
					})
				)
				.subscribe(({ users }) => {
					if (isMounted.current) {
						setCorpUsers(users);
					}
				});
		}
	}, [isOpen, upAccountId]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const users: ISearchUserElement[] = useMemo(() => {
		return corpUsers.map((corpUser) => ({
			id: corpUser.userId,
			value: corpUser.userName,
			content: (
				<div className={classes.menuItemWrapper}>
					<BodyS disableMargin>{corpUser.userName}</BodyS>
					{corpUser.departmentName && (
						<Badge filled color="primary" hexColor={corpUser.departmentUiColor}>
							{corpUser.departmentName}
						</Badge>
					)}
				</div>
			),
			departmentName: corpUser.departmentName,
			departmentUiColor: corpUser.departmentUiColor,
			userId: corpUser.userId,
		}));
		// eslint-disable-next-line
	}, [corpUsers]);

	const previousDepartment: IDepartmentData = useMemo(() => {
		return {
			name: corpUserData?.departmentName || '',
			color: corpUserData?.departmentUiColor || '',
		};
	}, [corpUserData]);

	const newDepartment: IDepartmentData = useMemo(() => {
		return {
			name: selectedCorpUser?.departmentName || '',
			color: selectedCorpUser?.departmentUiColor || '',
		};
	}, [selectedCorpUser]);

	return (
		<>
			<CustomDialog
				isOpen={isOpen}
				hideDialog={hideDialog}
				header={translate('numbers.details.assign.user')}
				dialogBody={
					<div>
						<NumberInfo phoneNumber={phoneNumber} accountDetails={accountDetails} />
						<div className={classes.inputWrapper}>
							<CustomSearch
								id="corpUsersSearch"
								elements={users}
								label={translate('number.details.user.name')}
								elementSelected={(user) => setSelectedCorpUser(user)}
								noResults={translate('no.results')}
								selectedElement={selectedCorpUser}
								loading={loading}
							/>
						</div>
					</div>
				}
				rightSideButtons={[
					{
						text: translate('cancel'),
						onClick: hideDialog,
						color: 'tertiary-light',
					},
					{
						text: translate('assign'),
						onClick: assign,
						color: 'primary-light',
						disabled: !selectedCorpUser,
					},
				]}
			/>
			<ConfirmationDialog
				isOpen={changingDepartmentDialog}
				hideDialog={() => setChangingDepartmentDialog(false)}
				header={translate('number.details.change.department')}
				dialogBody={
					<div className={classes.confirmationDialogBody}>
						<BodyS>{translate('number.details.same.department')}</BodyS>
						<BodyS>{translate('number.details.move.department', phoneNumber)}</BodyS>
						<div className={classes.changingDepartmentInfo}>
							<Phone className={classes.phoneIcon} />
							<Caption color="secondary-light">{phoneNumber}</Caption>
							<Badge filled hexColor={previousDepartment.color}>
								{previousDepartment.name}
							</Badge>
							<ArrowForward className={classes.arrowForwardIcon} />
							<Badge filled hexColor={newDepartment.color}>
								{newDepartment.name}
							</Badge>
						</div>
					</div>
				}
				buttons={[
					{
						text: translate('cancel'),
						onClick: cancel,
						color: 'tertiary-light',
					},
					{
						text: translate('change.it'),
						onClick: changeIt,
						color: 'primary-light',
					},
				]}
			/>
		</>
	);
};

export default AssignUserDialog;
